<template>
  <div class="profile-dropdown">
    <!-- Profile Button -->
    <button 
      class="pc-head-link profile-button" 
      @click="toggleDropdown"
    >
      <div class="avatar-wrapper">
        <img :src="profilePictureUrl || defaultAvatar" :alt="$t('common.user.avatarAlt')" class="user-avtar-logo">
        <div v-if="currentUser?.isImpersonating" class="header-impersonation-badge">
          <i class="ti ti-user-exclamation"></i>
        </div>
      </div>
    </button>

    <!-- Dropdown Menu -->
    <div 
      class="profile-dropdown-menu" 
      :class="{ 'show': isDropdownOpen }"
      @click.stop
    >
      <!-- Header -->
      <div class="dropdown-header">
        <h5 class="m-0">{{ $t('common.user.account') }}</h5>
      </div>

      <!-- Body -->
      <div class="dropdown-body">
        <div class="profile-scroll">
          <ul class="list-group list-group-flush w-100">
            <!-- User Info -->
            <li class="list-group-item">
              <div class="d-flex align-items-center">
                <div class="flex-shrink-0">
                  <div class="avatar-container">
                    <img :src="profilePictureUrl || defaultAvatar" :alt="$t('common.user.avatarAlt')" class="wid-50 rounded-circle">
                    <div v-if="currentUser?.isImpersonating" class="impersonation-badge">
                      <i class="ti ti-user-exclamation"></i>
                    </div>
                  </div>
                </div>
                <div class="flex-grow-1 mx-3">
                  <h5 class="mb-0">{{ currentUser?.tenantName || $t('common.user.tenant') }}</h5>
                 
                  <a class="link-primary" :href="`mailto:${currentUser?.email}`">{{ currentUser?.email }}</a>
                </div>
              </div>
            </li>
            
            <!-- Stop Impersonation (if active) -->
            <li v-if="currentUser?.isImpersonating" class="list-group-item">
              <a href="#" class="dropdown-item" @click.prevent="handleStopImpersonation">
                <span class="d-flex align-items-center">
                  <i class="ti ti-logout"></i>
                  <span>{{ $t('admin.stopImpersonation') }}</span>
                </span>
              </a>
            </li>
            
            <!-- Subscription status for supplier tenants; not during registration, when there
                 is no plan to upgrade until the account is approved. -->
            <li v-if="showSubscription" class="list-group-item">
              <div v-if="subscriptionLoading" class="text-center py-1">
                <span class="spinner-border spinner-border-sm text-primary"></span>
              </div>
              <template v-else-if="subscriptionStatus?.isActive">
                <div class="d-flex align-items-center gap-2 mb-1">
                  <i class="ti ti-credit-card text-success"></i>
                  <span class="fw-semibold small">{{ subscriptionStatus.productName }}</span>
                </div>
                <div class="small text-muted">
                  {{ $t('subscription.expiresOn') }}: {{ $formatDate(subscriptionStatus.endDate) }}
                </div>
              </template>
              <div v-else class="d-grid">
                <router-link to="/subscription/plans" class="btn btn-primary btn-sm" @click="closeDropdown">
                  <i class="ti ti-bolt me-1"></i>{{ $t('subscription.upgrade') }}
                </router-link>
              </div>
            </li>

            <!-- Menu Items -->
            <li v-if="!isBuyerTenant" class="list-group-item">
              <router-link to="/profile" class="dropdown-item" @click="closeDropdown">
                <span class="d-flex align-items-center">
                  <i class="ti ti-user-circle"></i>
                  <span>{{ $t('common.user.profile') }}</span>
                </span>
              </router-link>
              <router-link to="/settings/profile" class="dropdown-item" @click="closeDropdown">
                <span class="d-flex align-items-center">
                  <i class="ti ti-settings"></i>
                  <span>{{ $t('common.user.settings') }}</span>
                </span>
              </router-link>
            </li>
            
            <!-- Logout -->
            <li class="list-group-item">
              <a href="#" class="dropdown-item" @click.prevent="handleLogout">
                <span class="d-flex align-items-center">
                  <i class="ti ti-power"></i>
                  <span>{{ $t('common.logout') }}</span>
                </span>
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>

    <!-- Backdrop -->
    <div 
      v-if="isDropdownOpen" 
      class="profile-backdrop"
      @click="closeDropdown"
    ></div>
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted, onBeforeUnmount, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import Swal from 'sweetalert2';
import authService from '@/services/authService';
import fileManagementService from '@/services/fileManagementService';
import subscriptionService from '@/services/subscriptionService';
import { useFeatureFlags } from '@/composables/useFeatureFlags';
import { useOnboardingGate } from '@/composables/useOnboardingGate';
import defaultAvatarSrc from '@/assets/images/logo/vesproc40x40.png';

export default {
  name: 'ProfileDropdown',
  setup() {
    const store = useStore();
    const { t } = useI18n();
    const { features } = useFeatureFlags();

    const isDropdownOpen = ref(false);
    const isStoppingImpersonation = ref(false);
    const profilePictureUrl = ref('');
    const defaultAvatar = defaultAvatarSrc;
    const createdBlobUrls = [];
    const subscriptionStatus = ref(null);
    const subscriptionLoading = ref(false);

    const currentUser = computed(() => store.getters['auth/currentUser']);

    // Check if current user is a buyer tenant based on editionId
    const isBuyerTenant = computed(() => {
      const buyerTenantEditionId = '98CAF202-9D8E-B62F-01C1-39FF3E9F71A0';
      const userEditionId = currentUser.value?.editionId || localStorage.getItem('editionId');
      
      // Case-insensitive comparison
      return userEditionId?.toUpperCase() === buyerTenantEditionId.toUpperCase();
    });

    const userDisplayName = computed(() => {
      if (!currentUser.value) return '';
      
      // Show impersonated tenant/user information if impersonating
      if (currentUser.value.isImpersonating) {
        if (currentUser.value.isImpersonatingTenant) {
          const tenantName = currentUser.value.newTenantName || 
                             currentUser.value.impersonatedTenant?.name || 
                             t('common.user.impersonatedTenant');
          return t('common.user.impersonating', { name: tenantName });
        } else if (currentUser.value.impersonatedUser) {
          return t('common.user.impersonating', { name: currentUser.value.impersonatedUser.email });
        }
      }
      
      if (currentUser.value.firstName && currentUser.value.lastName) {
        return `${currentUser.value.firstName} ${currentUser.value.lastName}`;
      }
      
      return currentUser.value.email || t('common.user.title');
    });

    // null until resolved, so the upgrade button does not flash in during registration.
    const { isConfinedToOnboarding } = useOnboardingGate();
    const confinedToOnboarding = ref(null);

    const canHaveSubscription = computed(() =>
      features.subscription && !isBuyerTenant.value && !currentUser.value?.isSuperAdmin
    );
    const showSubscription = computed(() => canHaveSubscription.value && confinedToOnboarding.value === false);

    const loadSubscriptionStatus = async () => {
      if (!canHaveSubscription.value) return;
      confinedToOnboarding.value = await isConfinedToOnboarding();
      if (confinedToOnboarding.value) return;
      subscriptionLoading.value = true;
      try {
        subscriptionStatus.value = await subscriptionService.getCurrentStatus();
      } catch {
        // non-critical
      } finally {
        subscriptionLoading.value = false;
      }
    };

    const loadProfilePicture = async () => {
      try {
        const response = await fileManagementService.getProfilePicture();
        if (response?.success && response.blobUrl) {
          if (profilePictureUrl.value && profilePictureUrl.value !== defaultAvatar) {
            try { URL.revokeObjectURL(profilePictureUrl.value); } catch { /* Intentionally empty - ignore revoke errors */ }
          }
          profilePictureUrl.value = response.blobUrl;
          createdBlobUrls.push(response.blobUrl);
        } else {
          profilePictureUrl.value = defaultAvatar;
        }
      } catch {
        profilePictureUrl.value = defaultAvatar;
      }
    };

    const toggleDropdown = () => {
      isDropdownOpen.value = !isDropdownOpen.value;
    };

    const closeDropdown = () => {
      isDropdownOpen.value = false;
    };

    const handleLogout = async () => {
      closeDropdown();
      await logout();
    };

    const handleStopImpersonation = async () => {
      closeDropdown();
      await stopImpersonation();
    };

    const logout = async () => {
      try {
        const result = await Swal.fire({
          title: t('common.confirmations.logout.title'),
          text: t('common.confirmations.logout.message'),
          icon: 'question',
          showCancelButton: true,
          confirmButtonColor: '#3085d6',
          cancelButtonColor: '#6c757d',
          confirmButtonText: t('common.confirmations.logout.confirm'),
          cancelButtonText: t('common.confirmations.logout.cancel'),
          customClass: {
            confirmButton: 'btn btn-primary',
            cancelButton: 'btn btn-outline-secondary ml-1'
          },
          buttonsStyling: false
        });
        
        if (!result.isConfirmed) {
          return;
        }
        
        await store.dispatch('auth/logout');
        
        // Use window.location.href for a clean redirect that prevents any pending API calls
        window.location.href = '/login';
      } catch (error) {
        console.error('Logout error:', error);
        // Even on error, redirect to login page
        window.location.href = '/login';
      }
    };

    const stopImpersonation = async () => {
      isStoppingImpersonation.value = true;
      
      try {
        const result = await authService.stopImpersonation();
        
        store.commit('auth/SET_USER', result.user);
        store.commit('auth/SET_TOKEN', result.token);
        
        store.dispatch('notify', {
          message: t('common.notification.impersonationStopped'),
          type: 'success'
        });

        window.location.href = '/admin';
      } catch (err) {
        console.error('Stop impersonation error:', err);
        
        store.dispatch('notify', {
          message: err.response?.data?.error?.message || t('common.notification.stopImpersonationError'),
          type: 'error'
        });
      } finally {
        isStoppingImpersonation.value = false;
      }
    };

    // Close dropdown when clicking outside
    const handleClickOutside = (event) => {
      if (!event.target.closest('.profile-dropdown')) {
        closeDropdown();
      }
    };

    const handleProfilePicUpdated = () => {
      loadProfilePicture();
    };

    onMounted(() => {
      loadProfilePicture();
      loadSubscriptionStatus();
      document.addEventListener('click', handleClickOutside);
      window.addEventListener('profile-picture-updated', handleProfilePicUpdated);
    });

    // Reload when the subscription changes elsewhere (activate, cancel, resume).
    // Without this the dropdown keeps whatever it read at mount, so a tenant who
    // has just subscribed still sees "no subscription" until they sign out and in.
    watch(() => store.state.subscriptionRefreshKey, () => {
      loadSubscriptionStatus();
    });

    onUnmounted(() => {
      document.removeEventListener('click', handleClickOutside);
      window.removeEventListener('profile-picture-updated', handleProfilePicUpdated);
    });

    onBeforeUnmount(() => {
      createdBlobUrls.forEach(url => {
        if (url && url !== defaultAvatar) {
          try { URL.revokeObjectURL(url); } catch { /* Intentionally empty - ignore revoke errors */ }
        }
      });
    });

    return {
      isDropdownOpen,
      currentUser,
      isBuyerTenant,
      userDisplayName,
      profilePictureUrl,
      defaultAvatar,
      subscriptionStatus,
      subscriptionLoading,
      showSubscription,
      features,
      toggleDropdown,
      closeDropdown,
      handleLogout,
      handleStopImpersonation
    };
  }
};
</script>

<style scoped>
.profile-dropdown {
  position: relative;
}

.profile-button {
  position: relative;
  color: var(--bs-body-color);
  width: 40px;
  height: 40px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  border: none;
  background: transparent;
  transition: none;
  cursor: pointer;
  padding: 0;
}

.profile-button:focus {
  outline: none;
  box-shadow: none;
}

.avatar-wrapper {
  position: relative;
  width: 40px;
  height: 40px;
}

.user-avtar-logo {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
  transition: all 0.3s ease;
  border: 2px solid transparent;
  position: relative;
  z-index: 1;
}

.profile-button:hover .user-avtar-logo {
  border-color: var(--bs-primary);
  box-shadow: 0 0 0 3px rgba(0, 160, 223, 0.15);
  transform: scale(1.08);
}

/* Header Impersonation Badge */
.header-impersonation-badge {
  position: absolute;
  top: -4px;
  right: -4px;
  width: 20px;
  height: 20px;
  background: linear-gradient(135deg, #ff6b35 0%, #f7931e 100%);
  border: 2px solid white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  color: white;
  animation: pulse-impersonation 2s infinite;
  z-index: 2;
  box-shadow: 0 2px 8px rgba(255, 107, 53, 0.4);
}

@keyframes pulse-impersonation {
  0% {
    transform: scale(1);
    box-shadow: 0 2px 8px rgba(255, 107, 53, 0.4);
  }
  50% {
    transform: scale(1.1);
    box-shadow: 0 4px 12px rgba(255, 107, 53, 0.6);
  }
  100% {
    transform: scale(1);
    box-shadow: 0 2px 8px rgba(255, 107, 53, 0.4);
  }
}

/* Dropdown Menu */
.profile-dropdown-menu {
  position: absolute;
  top: 100%;
  right: 0;
  min-width: 320px;
  max-width: 360px;
  background: white;
  border: 1px solid var(--bs-border-color);
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  z-index: 1050;
  opacity: 0;
  visibility: hidden;
  transform: translateY(-10px);
  transition: all 0.3s ease;
  margin-top: 8px;
}

.profile-dropdown-menu.show {
  opacity: 1;
  visibility: visible;
  transform: translateY(0);
}

.dropdown-header {
  padding: 1rem 1.25rem;
  border-bottom: 1px solid var(--bs-border-color);
  background: #fff;
  border-radius: 8px 8px 0 0;
}

.dropdown-header h5 {
  font-size: 1rem;
  font-weight: 600;
  color: var(--bs-body-color);
}

.dropdown-body {
  padding: 0;
}

.profile-scroll {
  max-height: calc(100vh - 225px);
  overflow-y: auto;
}

/* Custom scrollbar */
.profile-scroll::-webkit-scrollbar {
  width: 6px;
}

.profile-scroll::-webkit-scrollbar-track {
  background: transparent;
}

.profile-scroll::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.2);
  border-radius: 10px;
}

.profile-scroll::-webkit-scrollbar-thumb:hover {
  background: rgba(0, 0, 0, 0.3);
}

.list-group-item {
  border: none;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  padding: 0.75rem 1.25rem;
  background: transparent;
}

.list-group-item:last-child {
  border-bottom: none;
}

.dropdown-item {
  padding: 0.5rem 0;
  color: var(--bs-body-color);
  display: flex;
  align-items: center;
  border-radius: 4px;
  transition: all 0.2s ease;
  text-decoration: none;
}

.dropdown-item:hover {
  background: rgba(0, 160, 223, 0.08);
  color: var(--bs-primary);
  padding-left: 0.5rem;
}

.dropdown-item span {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
}

.dropdown-item i {
  font-size: 1.25rem;
  width: 24px;
  text-align: center;
}

/* User info section */
.wid-50 {
  width: 50px;
  height: 50px;
  object-fit: cover;
}

.avatar-container {
  position: relative;
}

.impersonation-badge {
  position: absolute;
  top: -2px;
  right: -2px;
  width: 18px;
  height: 18px;
  background: #ff6b35;
  border: 2px solid white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  color: white;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(255, 107, 53, 0.7);
  }
  70% {
    transform: scale(1.1);
    box-shadow: 0 0 0 5px rgba(255, 107, 53, 0);
  }
  100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(255, 107, 53, 0);
  }
}

.link-primary {
  color: var(--bs-primary);
  text-decoration: none;
  font-size: 0.875rem;
}

.link-primary:hover {
  color: var(--bs-primary);
  text-decoration: underline;
}

/* Profile Backdrop */
.profile-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1040;
}

/* Responsive */
@media (max-width: 480px) {
  .profile-dropdown-menu {
    width: 300px;
    right: -20px;
  }
}
</style>

